import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import produce from 'immer';
import { get as lodashGet, compact } from 'lodash-es';
import { QueryKeys } from '~/hooks/reactQuery/queryKeys';
import { ErrorResponse } from '~/infra/ErrorResponse';
import { extractClaimsFromToken, handlePromise, wait } from '~/infra/utils';
import { mergeIntegration } from '~/integrations/utils/mergeIntegration';
import { UserInfoModel } from '~/models';
import { queryClient } from '~/queryClient';
import { getVisibleNotifications } from '~/types/api/in-app-notifications.utils';
import { discardMeBootPrefetch, takeMeBootPrefetch } from './me-boot-prefetch';
import { selectors } from './state-selectors';
import { defaultFeatureFlagsKey, getDefaultInitialState, getOrCreateStoreLocation, initMakeFetchAndStoreFunction } from './store.utils';
export const FUTURE_INVOICE_LOOKAHEAD_MONTHS = 16;
export function initStoreActions(param) {
    let { api, getState, setState, onLogBI, getNow } = param;
    const makeFetchAndStoreFunction = initMakeFetchAndStoreFunction({
        setState,
        getState,
        getNow
    });
    /** Resets the store state */ function reset(authFlowDone) {
        setState(getDefaultInitialState(_object_spread({}, getState().session), {
            authFlowDone,
            featureFlags: getState().ff.featureFlags
        }), false, 'reset');
    }
    /** Resets the business data. used when switching businesses  */ function resetBusinessData() {
        const _getDefaultInitialState = getDefaultInitialState(getState().session), { session: _1, authToken: _2, authFlowDone: _3, claims: _4, userInfo: _5, userClientData: _6, ff: _7 } = _getDefaultInitialState, resetData = _object_without_properties(_getDefaultInitialState, [
            "session",
            "authToken",
            "authFlowDone",
            "claims",
            "userInfo",
            "userClientData",
            "ff"
        ]);
        setState(resetData, false, 'resetBusinessData');
    }
    /**
     * Starts a new request. baseLocation is the key in the store or an array that
     * represents a path in the store.
     */ function requestStart(baseLocation) {
        setState(produce((state)=>{
            const loc = getOrCreateStoreLocation(state, baseLocation);
            loc.loading = true;
        }), false, "requestStart ".concat(JSON.stringify(baseLocation)));
    }
    /**
     * Ends a request successfully with response data. baseLocation is the key in the
     * store or an array that represents a path in the store.
     */ // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function requestSuccess(baseLocation, data) {
        setState(produce((state)=>{
            const loc = getOrCreateStoreLocation(state, baseLocation);
            loc.loading = false;
            loc.lastSuccessfulFetch = getNow().getTime();
            loc.data = data;
            loc.error = null;
        }), false, "requestSuccess ".concat(JSON.stringify(baseLocation)));
    }
    /**
     * Ends a request with an error. baseLocation is the key in the store or an array that
     * represents a path in the store.
     */ // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function requestFail(baseLocation, error) {
        setState(produce((state)=>{
            const loc = getOrCreateStoreLocation(state, baseLocation);
            loc.loading = false;
            loc.data = null;
            loc.error = error;
        }), false, "requestFail ".concat(JSON.stringify(baseLocation)));
    }
    function setRelationshipVendorId(id) {
        setState({
            ff: _object_spread_props(_object_spread({}, getState().ff), {
                relationshipVendorId: id
            })
        }, false, 'setRelationshipVendorId');
    }
    /**
     * Fetches the feature-flags and saves in the store. If given a businessId will fetch
     * the feature-flags for that businessId. Otherwise, will fetch global feature-flags.
     */ async function fetchFeatureFlags(businessId, authToken) {
        const storeLocation = [
            'ff',
            'featureFlags',
            businessId || defaultFeatureFlagsKey
        ];
        const featureFlags = lodashGet(getState(), storeLocation);
        if (featureFlags && (featureFlags.loading || featureFlags.data)) {
            // No need to load twice if data exists or a request is already in progress.
            return;
        }
        requestStart(storeLocation);
        try {
            const featureFlags = await api.featureFlag.getAll(businessId, authToken);
            requestSuccess(storeLocation, featureFlags);
        } catch (err) {
            requestFail(storeLocation, err);
        }
    }
    async function resetStoreAndFinishAuthFlow() {
        let opts = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
        var _opts_authError;
        reset();
        await fetchFeatureFlags();
        setState({
            authFlowDone: true,
            authError: (_opts_authError = opts.authError) !== null && _opts_authError !== void 0 ? _opts_authError : null
        });
    }
    async function fetchUserInfoV2ConsumingBootPrefetch(authToken) {
        const prefetchedUserInfo = takeMeBootPrefetch({
            authToken,
            apiGatewayHost: getState().session.apiGatewayHost
        });
        const dto = prefetchedUserInfo == null ? null : await prefetchedUserInfo;
        return dto == null ? api.auth.getUserInfoV2() : UserInfoModel.fromDTO(dto);
    }
    function fetchUserInfoOldDiscardingBootPrefetch() {
        discardMeBootPrefetch();
        return api.auth.getUserInfo();
    }
    let vendorScopedBackgroundFetches = null;
    function startVendorScopedBackgroundFetches(businessIdFromUserInfo) {
        return Promise.all(compact([
            handlePromise(api.auth.getUserVideos().then(setUserVideos)),
            businessIdFromUserInfo && handlePromise(api.business.getInfo(businessIdFromUserInfo).then((info)=>{
                setBusinessInfo(info);
                queryClient.setQueryData([
                    QueryKeys.GetInfo,
                    businessIdFromUserInfo
                ], info);
            })),
            handlePromise(fetchIntegrations(businessIdFromUserInfo))
        ]));
    }
    async function setAuthToken(authToken) {
        if (vendorScopedBackgroundFetches) {
            await vendorScopedBackgroundFetches;
            vendorScopedBackgroundFetches = null;
        }
        const claims = extractClaimsFromToken(authToken);
        if (authToken == null || claims == null) {
            return await resetStoreAndFinishAuthFlow();
        }
        if (selectors.isResetPasswordFlow({
            claims
        })) {
            await fetchFeatureFlags(selectors.user.businessId(getState()), authToken);
            setState({
                authToken,
                claims,
                authFlowDone: true
            });
            return;
        }
        setState({
            claims,
            authToken
        });
        const { ME_V2_EXCLUDE_ACCOUNTANT_WORKSPACES } = selectors.ff.featureFlagsData(getState());
        const userInfoPromise = handlePromise(ME_V2_EXCLUDE_ACCOUNTANT_WORKSPACES ? fetchUserInfoV2ConsumingBootPrefetch(authToken) : fetchUserInfoOldDiscardingBootPrefetch());
        const claimsBusinessId = claims.businessId || undefined;
        // WHY: these fetches need only the token-borne businessId, so they run concurrently with /me instead of after it.
        const claimsScopedFetches = compact([
            handlePromise(fetchUserClientData()),
            claimsBusinessId && handlePromise(fetchBusinessClientData(claimsBusinessId)),
            claimsBusinessId && fetchFeatureFlags(claimsBusinessId)
        ]);
        const userInfoResponse = await userInfoPromise;
        if (!userInfoResponse.success) {
            // WHY: reset must be the last state write on a failed boot — wait for the concurrent fetches to settle first.
            await Promise.all(claimsScopedFetches);
            const status = new ErrorResponse(userInfoResponse.error).status;
            const isExpiredToken = status === 401;
            await resetStoreAndFinishAuthFlow(isExpiredToken ? {} : {
                authError: {
                    kind: 'serverError',
                    status
                }
            });
            throw new Error('Failed to get user info');
        }
        const userInfo = userInfoResponse.data;
        if (userInfo == null) {
            await Promise.all(claimsScopedFetches);
            return await resetStoreAndFinishAuthFlow();
        }
        const isVendor = userInfo.isVendor();
        const businessIdFromUserInfo = userInfo.businessId();
        const userInfoBusinessIdDiffersFromClaims = !!businessIdFromUserInfo && businessIdFromUserInfo !== claimsBusinessId;
        const tokenMissingExistingBusiness = !claimsBusinessId && !!businessIdFromUserInfo;
        if (tokenMissingExistingBusiness) {
            var _extractClaimsFromToken;
            const remintedToken = await handlePromise(api.user.switchBusiness(businessIdFromUserInfo));
            if (getState().authToken !== authToken) {
                await Promise.all(claimsScopedFetches);
                return;
            }
            const remintedTokenValue = remintedToken.success ? remintedToken.data : null;
            if (remintedTokenValue != null && ((_extractClaimsFromToken = extractClaimsFromToken(remintedTokenValue)) === null || _extractClaimsFromToken === void 0 ? void 0 : _extractClaimsFromToken.businessId)) {
                await Promise.all(claimsScopedFetches);
                return await setAuthToken(remintedTokenValue);
            }
        }
        if (isVendor) {
            fetchAccountantAccessBusinessesInBackground();
            vendorScopedBackgroundFetches = startVendorScopedBackgroundFetches(businessIdFromUserInfo);
        }
        await Promise.all(compact([
            ...claimsScopedFetches,
            userInfoBusinessIdDiffersFromClaims && handlePromise(fetchBusinessClientData(businessIdFromUserInfo)),
            userInfoBusinessIdDiffersFromClaims && fetchFeatureFlags(businessIdFromUserInfo)
        ]));
        setUserInfo(userInfo);
        // Necessary for the store to update before the next render cycle, so useFeatureFlags
        // will get the ones from the user's business instead of the default ones.
        await wait(0);
        setState({
            authFlowDone: true
        });
    }
    function fetchCurrentUserInfo() {
        const { ME_V2_EXCLUDE_ACCOUNTANT_WORKSPACES } = selectors.ff.featureFlagsData(getState());
        return ME_V2_EXCLUDE_ACCOUNTANT_WORKSPACES ? api.auth.getUserInfoV2() : api.auth.getUserInfo();
    }
    async function fetchUserInfo() {
        const userInfo = await fetchCurrentUserInfo();
        setUserInfo(userInfo);
        if (userInfo === null || userInfo === void 0 ? void 0 : userInfo.isVendor()) {
            fetchAccountantAccessBusinessesInBackground();
        }
        return userInfo;
    }
    function fetchAccountantAccessBusinessesInBackground() {
        const { ME_V2_EXCLUDE_ACCOUNTANT_WORKSPACES } = selectors.ff.featureFlagsData(getState());
        if (!ME_V2_EXCLUDE_ACCOUNTANT_WORKSPACES) return;
        const authTokenAtFetch = getState().authToken;
        api.auth.getAccountantAccessBusinesses().then((businessIds)=>{
            if (getState().authToken === authTokenAtFetch) {
                setState({
                    accountantAccessBusinessIds: new Set(businessIds)
                });
            }
        }).catch(()=>{});
    }
    function setUserInfo(userInfo) {
        var _ref, _ref1;
        var _getState_userInfo;
        const previousJwt = (_ref = (_getState_userInfo = getState().userInfo) === null || _getState_userInfo === void 0 ? void 0 : _getState_userInfo.intercomUserJwt()) !== null && _ref !== void 0 ? _ref : null;
        const nextJwt = (_ref1 = userInfo === null || userInfo === void 0 ? void 0 : userInfo.intercomUserJwt()) !== null && _ref1 !== void 0 ? _ref1 : null;
        const jwtChanged = nextJwt != null && nextJwt !== previousJwt;
        setState(jwtChanged ? {
            userInfo,
            intercomUserJwtReceivedAt: getNow().getTime()
        } : {
            userInfo
        }, false, 'setUserInfo');
    }
    async function refreshIntercomJwt() {
        try {
            const fresh = await fetchCurrentUserInfo();
            if (fresh == null) {
                return {
                    receivedUserInfo: false,
                    jwtPresentInResponse: false,
                    httpStatus: null,
                    errorMessage: null
                };
            }
            const jwt = fresh.intercomUserJwt();
            const current = getState().userInfo;
            if (jwt != null && current != null) {
                setUserInfo(current.setIntercomUserJwt(jwt));
            }
            return {
                receivedUserInfo: true,
                jwtPresentInResponse: jwt != null,
                httpStatus: 200,
                errorMessage: null
            };
        } catch (error) {
            const errorResponse = new ErrorResponse(error);
            return {
                receivedUserInfo: false,
                jwtPresentInResponse: false,
                httpStatus: errorResponse.status,
                errorMessage: errorResponse.serverErrorCode()
            };
        }
    }
    const setUserVideos = (videos)=>{
        setState({
            videos
        }, false, 'videos');
    };
    const setBusinessInfo = (businessInfo)=>setState({
            businessInfo
        });
    const markHasSentAProposal = (isSampleRelationship)=>{
        const { businessInfo } = getState();
        if (!businessInfo || businessInfo.hasSentAProposal) return;
        setState({
            businessInfo: _object_spread({}, businessInfo, isSampleRelationship ? {
                hasSentASampleProposal: true
            } : {
                hasSentAProposal: true
            })
        });
    };
    function setWorksAt(worksAt) {
        setState((state)=>({
                userInfo: state.userInfo ? state.userInfo.setWorksAt(worksAt) : null
            }), false, 'setWorksAt');
    }
    function setUserClientData(userClientData) {
        setState({
            userClientData
        }, false, 'setUserClientData');
    }
    function setWorkspaces(workspaces) {
        setState((state)=>({
                userInfo: state.userInfo ? state.userInfo.setWorkspaceModels(workspaces) : null
            }), false, 'setWorkspaces');
    }
    function setBusinessClientData(data, _businessId) {
        const businessId = _businessId || selectors.user.businessId(getState());
        if (!businessId) return;
        setState({
            businessClientData: _object_spread_props(_object_spread({}, getState().businessClientData), {
                [businessId]: data
            })
        }, false, 'setBusinessClientData');
    }
    function setStripeLoadingFailed(stripeLoadingFailed) {
        setState({
            stripeLoadingFailed
        }, false, 'setStripeLoadingFailed');
    }
    function setInAppNotificationsDrawerOpen(inAppNotificationsDrawerOpen) {
        setState({
            inAppNotificationsDrawerOpen
        }, false, 'inAppNotificationsDrawerOpen');
        if (inAppNotificationsDrawerOpen) {
            setState({
                showNotificationPimple: false
            }, false, 'newNotificationsCounter');
        }
    }
    function setIntegration(integration) {
        var _selectors_user_business;
        const newIntegrations = mergeIntegration(getState().integrations.data || [], integration);
        setState({
            integrations: _object_spread_props(_object_spread({}, getState().integrations), {
                data: newIntegrations
            })
        });
        const businessId = (_selectors_user_business = selectors.user.business(getState())) === null || _selectors_user_business === void 0 ? void 0 : _selectors_user_business.id;
        if (businessId) {
            queryClient.setQueryData([
                QueryKeys.Integrations,
                businessId
            ], (current)=>mergeIntegration(current !== null && current !== void 0 ? current : [], integration));
        }
    }
    async function updateBusinessColor(color) {
        const worksAt = selectors.user.business(getState());
        if (worksAt) {
            await api.vendor.updateBusinessDetails(worksAt.id, {
                color
            });
            setWorksAt(Object.assign({}, worksAt, {
                branding: {
                    color
                }
            }));
        }
    }
    async function fetchFinancialCentsItems() {
        const storeLocation = [
            'financialCents',
            'items'
        ];
        requestStart(storeLocation);
        try {
            const response = await api.financialCents.getItems();
            requestSuccess(storeLocation, response);
            return response;
        } catch (err) {
            requestFail(storeLocation, err);
            throw err;
        }
    }
    async function fetchKeeperItems() {
        const storeLocation = [
            'keeper',
            'items'
        ];
        requestStart(storeLocation);
        try {
            const response = await api.keeper.getItems();
            requestSuccess(storeLocation, response);
            return response;
        } catch (err) {
            requestFail(storeLocation, err);
            throw err;
        }
    }
    async function fetchKarbonItems() {
        const storeLocation = [
            'karbon',
            'items'
        ];
        requestStart(storeLocation);
        try {
            const response = await api.karbon.getItems();
            requestSuccess(storeLocation, response);
            return response;
        } catch (err) {
            requestFail(storeLocation, err);
            throw err;
        }
    }
    async function fetchClientHubItems() {
        const storeLocation = [
            'clientHub',
            'items'
        ];
        requestStart(storeLocation);
        try {
            const response = await api.clientHub.getItems();
            requestSuccess(storeLocation, response);
            return response;
        } catch (err) {
            requestFail(storeLocation, err);
            throw err;
        }
    }
    async function fetchFinancialCentsUsers() {
        const storeLocation = [
            'financialCents',
            'users'
        ];
        requestStart(storeLocation);
        try {
            const response = await api.financialCents.getUsers();
            requestSuccess(storeLocation, response);
            return response;
        } catch (err) {
            requestFail(storeLocation, err);
            throw err;
        }
    }
    async function fetchKarbonUsers() {
        const storeLocation = [
            'karbon',
            'users'
        ];
        requestStart(storeLocation);
        try {
            const response = await api.karbon.getUsers();
            requestSuccess(storeLocation, response);
            return response;
        } catch (err) {
            requestFail(storeLocation, err);
            throw err;
        }
    }
    const fetchIntegrationsBase = makeFetchAndStoreFunction({
        fetcher: api.integration.getAll,
        getLoadableContent: (state)=>state.integrations,
        allowStaleDataFor: 2000
    });
    const fetchIntegrations = async (bootBusinessId)=>{
        var _selectors_user_business;
        const integrations = await fetchIntegrationsBase();
        const businessId = bootBusinessId !== null && bootBusinessId !== void 0 ? bootBusinessId : (_selectors_user_business = selectors.user.business(getState())) === null || _selectors_user_business === void 0 ? void 0 : _selectors_user_business.id;
        if (businessId && integrations) {
            queryClient.setQueryData([
                QueryKeys.Integrations,
                businessId
            ], integrations);
        }
        return integrations;
    };
    const fetchIntegrationsSummaryBase = makeFetchAndStoreFunction({
        fetcher: api.integration.getSummary,
        getLoadableContent: (state)=>state.integrationsSummary,
        allowStaleDataFor: 2000
    });
    const fetchIntegrationsSummary = async ()=>{
        var _selectors_user_business;
        const integrationsSummary = await fetchIntegrationsSummaryBase();
        const businessId = (_selectors_user_business = selectors.user.business(getState())) === null || _selectors_user_business === void 0 ? void 0 : _selectors_user_business.id;
        if (businessId && integrationsSummary) {
            queryClient.setQueryData([
                QueryKeys.IntegrationsSummary,
                businessId
            ], integrationsSummary);
        }
        return integrationsSummary;
    };
    async function fetchKarbonIntegration() {
        const storeLocation = 'integrations';
        requestStart(storeLocation);
        try {
            var _selectors_user_business;
            const currentIntegrations = getState().integrations;
            if (!currentIntegrations.data) {
                return fetchIntegrations();
            }
            const integration = await api.karbon.getIntegration();
            const newIntegrations = mergeIntegration(currentIntegrations.data, integration);
            requestSuccess(storeLocation, newIntegrations);
            const businessId = (_selectors_user_business = selectors.user.business(getState())) === null || _selectors_user_business === void 0 ? void 0 : _selectors_user_business.id;
            if (businessId) {
                queryClient.setQueryData([
                    QueryKeys.Integrations,
                    businessId
                ], (current)=>mergeIntegration(current !== null && current !== void 0 ? current : [], integration));
            }
            return integration;
        } catch (err) {
            requestFail(storeLocation, err);
        }
    }
    async function fetchInAppNotifications() {
        requestStart('inAppNotifications');
        try {
            const { pushedNotifications } = await api.inAppNotifications.getAll();
            requestSuccess('inAppNotifications', pushedNotifications);
            const inAppNotificationsDrawerOpen = getState().inAppNotificationsDrawerOpen;
            const hasUnseenVisibleNotification = getVisibleNotifications(pushedNotifications).some((n)=>!n.seen);
            const shouldShowPimple = !inAppNotificationsDrawerOpen && hasUnseenVisibleNotification;
            setState({
                showNotificationPimple: shouldShowPimple
            }, false, 'newNotificationsCounter');
        } catch (err) {
            requestFail('inAppNotifications', err);
        }
    }
    async function fetchUserClientData() {
        const data = await api.user.getUserClientData();
        setUserClientData(data);
        return data;
    }
    async function fetchBusinessClientData(businessId) {
        const data = await api.user.getBusinessClientData();
        setBusinessClientData(data, businessId);
        return data;
    }
    async function updateBusinessClientData(businessId, changes) {
        let options = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : {};
        const { useLocalData } = options;
        const currentData = useLocalData ? selectors.user.businessClientData(getState(), businessId) : await fetchBusinessClientData();
        const newData = _object_spread({}, currentData, changes);
        await api.user.setBusinessClientData(newData);
        setBusinessClientData(newData);
        return newData;
    }
    async function fetchActivityLog(relationshipId) {
        const storeLocation = [
            'activityLog',
            relationshipId
        ];
        requestStart(storeLocation);
        try {
            const res = await api.activityLog.getOld(relationshipId);
            requestSuccess(storeLocation, res);
            return res;
        } catch (err) {
            requestFail(storeLocation, err);
        }
    }
    async function updateUserClientData(changes) {
        let options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
        const { useLocalData } = options;
        const currentData = useLocalData ? getState().userClientData : await fetchUserClientData();
        const newData = _object_spread({}, currentData, changes);
        await api.user.setUserClientData(newData);
        setUserClientData(newData);
        return newData;
    }
    /** Logs a BI event. */ function logBI(id, data) {
        const state = getState();
        onLogBI(id, _object_spread({
            userId: selectors.user.id(state),
            businessId: selectors.user.businessId(state)
        }, data || {}));
    }
    function setBranding(branding) {
        var _getState_userInfo;
        setState({
            userInfo: ((_getState_userInfo = getState().userInfo) === null || _getState_userInfo === void 0 ? void 0 : _getState_userInfo.setBranding(branding)) || null
        }, false, 'setBranding');
    }
    function setQueryAsLoading(queryKey, isLoading) {
        setState({
            isQueryLoading: _object_spread_props(_object_spread({}, getState().isQueryLoading), {
                [queryKey]: isLoading
            })
        }, false, "setQueryAsHardLoading ".concat(queryKey, " ").concat(isLoading));
    }
    function setLocationStack(locations) {
        setState({
            locationStack: locations
        }, false, 'setLocationStack');
    }
    function setIsSwitchingBusiness(isSwitchingBusiness) {
        setState({
            isSwitchingBusiness
        }, false, 'setIsSwitchingBusiness');
    }
    function authorizePusherChannel(params) {
        return api.pusher.authorizeChannel(params);
    }
    function searchPlaces(params) {
        return api.places.autocomplete(params);
    }
    function getPlaceDetails(params) {
        return api.places.details(params);
    }
    return {
        authorizePusherChannel,
        searchPlaces,
        getPlaceDetails,
        reset,
        resetBusinessData,
        requestStart,
        requestSuccess,
        requestFail,
        setAuthToken,
        fetchUserInfo,
        refreshIntercomJwt,
        setUserVideos,
        setUserInfo,
        setWorksAt,
        setWorkspaces,
        setUserClientData,
        setStripeLoadingFailed,
        setInAppNotificationsDrawerOpen,
        setIntegration,
        setBusinessInfo,
        markHasSentAProposal,
        updateBusinessColor,
        fetchIntegrations,
        fetchIntegrationsSummary,
        fetchInAppNotifications,
        fetchUserClientData,
        fetchBusinessClientData,
        fetchActivityLog,
        fetchFinancialCentsItems,
        fetchKeeperItems,
        fetchFinancialCentsUsers,
        fetchKarbonUsers,
        updateBusinessClientData,
        updateUserClientData,
        logBI,
        setBranding,
        fetchKarbonItems,
        fetchKarbonIntegration,
        fetchClientHubItems,
        ff: {
            setRelationshipVendorId,
            fetchFeatureFlags
        },
        api,
        setQueryAsLoading,
        setLocationStack,
        setIsSwitchingBusiness
    };
}
